/**
 * Multiline plain text with native form participation and optional autosizing.
 * @element qds-textarea
 * @attr {string} value - Default/reset value; property value is the current value.
 * @attr {string} name - Submitted field name.
 * @attr {string} label - Visible label.
 * @attr {string} aria-label - Accessible name when a visible label is supplied elsewhere.
 * @attr {string} hint - Helper text.
 * @attr {string} error-text - Validation message override.
 * @attr {string} placeholder - Example text, not a substitute for a label.
 * @attr {string} autocomplete - Browser autocomplete hint.
 * @attr {boolean} disabled - Not interactive or submitted.
 * @attr {boolean} readonly - Submitted but not editable.
 * @attr {boolean} required - Requires non-empty text.
 * @attr {boolean} invalid - Author-supplied invalid state.
 * @attr {number} rows - Minimum visible rows; default 3.
 * @attr {boolean} autosize - Grow/shrink with content within max-rows.
 * @attr {number} max-rows - Autosize row ceiling; default 12.
 * @attr {number} minlength - Minimum text length.
 * @attr {number} maxlength - Maximum text length and native typing limit.
 * @attr {boolean} show-counter - Show current length and optional maximum.
 * @attr {'none'|'vertical'|'horizontal'|'both'} resize - Manual resize policy; default vertical.
 * @attr {'sm'|'md'|'lg'} size - Field size.
 * @attr {'blur'|'input'|'submit'} validate-on - Error display timing; default blur.
 * @property {string} value - Current plain text; programmatic changes do not emit input/change.
 * @property {string} defaultValue - Attribute-backed reset value.
 * @csspart container - Field container.
 * @csspart label - Native label.
 * @csspart input - Native textarea.
 * @csspart hint - Helper text.
 * @csspart error - Validation text.
 * @csspart counter - Character counter.
 * @fires input - Every user edit; detail { component, value }.
 * @fires change - Committed native change; detail { component, value }.
 * @fires qds-validity-change - Validity or message change.
 */
import { QdsNativeField, type FieldValidity } from '../shared/native-field.js';
import { isAttrTruthy, reflectBoolean } from '../shared/form-control.js';

export class QdsTextarea extends QdsNativeField {
  static override get observedAttributes(): string[] {
    return [...super.observedAttributes, 'value', 'placeholder', 'autocomplete', 'rows', 'max-rows', 'autosize', 'minlength', 'maxlength', 'show-counter', 'resize'];
  }
  private _value = '';
  private _resizeObserver?: ResizeObserver;
  private _lastWidth = -1;
  protected override get _upgradeNames(): string[] { return ['value', 'defaultValue', 'placeholder', 'autocomplete', 'rows', 'maxRows', 'autosize', 'minlength', 'maxlength', 'showCounter', 'resize']; }
  get value(): string { return this._value; }
  set value(value: string) { this._dirty = true; this._value = String(value ?? ''); this._sync(); }
  get defaultValue(): string { return this.getAttribute('value') || ''; }
  set defaultValue(value: string) { this.setAttribute('value', String(value ?? '')); }
  get placeholder(): string { return this.getAttribute('placeholder') || ''; }
  set placeholder(value: string) { this.setAttribute('placeholder', value); }
  get autocomplete(): string { return this.getAttribute('autocomplete') || ''; }
  set autocomplete(value: string) { this.setAttribute('autocomplete', value); }
  private _number(name: string, fallback: number, minimum: number): number {
    const text = this.getAttribute(name);
    const number = text === null ? NaN : Number(text);
    return Number.isInteger(number) && number >= minimum ? number : fallback;
  }
  get rows(): number { return this._number('rows', 3, 1); }
  set rows(value: number) { this.setAttribute('rows', String(value)); }
  get maxRows(): number { return Math.max(this.rows, this._number('max-rows', 12, 1)); }
  set maxRows(value: number) { this.setAttribute('max-rows', String(value)); }
  get minlength(): number { return this._number('minlength', -1, 0); }
  set minlength(value: number) { if (value < 0) this.removeAttribute('minlength'); else this.setAttribute('minlength', String(value)); }
  get maxlength(): number { return this._number('maxlength', -1, 0); }
  set maxlength(value: number) { if (value < 0) this.removeAttribute('maxlength'); else this.setAttribute('maxlength', String(value)); }
  get autosize(): boolean { return isAttrTruthy(this.getAttribute('autosize')); }
  set autosize(value: boolean) { reflectBoolean(this, 'autosize', value); }
  get showCounter(): boolean { return isAttrTruthy(this.getAttribute('show-counter')); }
  set showCounter(value: boolean) { reflectBoolean(this, 'show-counter', value); }
  get resize(): 'none' | 'vertical' | 'horizontal' | 'both' {
    const value = this.getAttribute('resize');
    return value === 'none' || value === 'horizontal' || value === 'both' ? value : 'vertical';
  }
  set resize(value: string) { this.setAttribute('resize', value); }
  override attributeChangedCallback(): void { if (!this._dirty) this._value = this.defaultValue; super.attributeChangedCallback(); }
  protected _render(): void {
    if (!this._dirty) this._value = this.defaultValue;
    this.shadowRoot!.innerHTML = `<style>${this._styles}
      :host { width: 100%; } textarea.qds-control-surface { display: block; width: 100%; height: auto; min-height: 44px; padding: 10px 12px; line-height: 1.5; }
      .qds-counter { display: block; text-align: end; font-size: 12px; margin-top: 4px; }
    </style><div part="container"><label class="qds-label" part="label" for="${this._id}"><span class="label-text"></span><span class="qds-required-marker" aria-hidden="true">*</span></label><textarea id="${this._id}" class="qds-control-surface" part="input"></textarea><div class="qds-feedback"><div class="qds-hint" part="hint" id="${this._id}-hint"></div><div class="qds-error" part="error" id="${this._id}-error" aria-live="polite"></div></div><span class="qds-counter" part="counter" id="${this._id}-counter"></span></div>`;
    this._control = this.shadowRoot!.querySelector('textarea')!;
  }
  protected _syncControl(): void {
    const input = this._control as HTMLTextAreaElement;
    if (input.value !== this.value) input.value = this.value;
    input.rows = this.rows;
    input.placeholder = this.placeholder;
    input.setAttribute('autocomplete', this.autocomplete);
    for (const name of ['minlength', 'maxlength'] as const) {
      if (this[name] >= 0) input.setAttribute(name, String(this[name])); else input.removeAttribute(name);
    }
    input.style.resize = this.autosize ? 'none' : this.resize;
    const counter = this.shadowRoot!.querySelector('.qds-counter') as HTMLElement;
    counter.hidden = !this.showCounter;
    counter.textContent = this.maxlength >= 0 ? `${this.value.length} / ${this.maxlength}` : String(this.value.length);
    this._autosize();
  }
  protected override _counterDescription(): string { return this.showCounter ? `${this._id}-counter` : ''; }
  private _autosize(): void {
    const input = this._control as HTMLTextAreaElement | undefined;
    if (!input) return;
    if (!this.autosize) { input.style.height = ''; input.style.overflowY = ''; return; }
    const style = getComputedStyle(input);
    const line = parseFloat(style.lineHeight) || 21;
    const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    const border = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
    input.style.height = 'auto';
    const ceiling = this.maxRows * line + padding + border;
    const height = Math.min(ceiling, Math.max(this.rows * line + padding + border, input.scrollHeight + border));
    input.style.height = `${height}px`;
    input.style.overflowY = input.scrollHeight + border > ceiling ? 'auto' : 'hidden';
  }
  private _onInput = (event: Event): void => {
    event.stopPropagation();
    if (this.disabled || this.readonly) { this._sync(); return; }
    this._dirty = true;
    this._value = this._control!.value;
    if (this.validateOn === 'input') this._showError = true;
    this._sync();
    this._emit('input', { value: this.value });
  };
  private _onChange = (event: Event): void => { event.stopPropagation(); if (!this.disabled && !this.readonly) this._emit('change', { value: this.value }); };
  protected _bind(): void {
    this._control!.addEventListener('input', this._onInput);
    this._control!.addEventListener('change', this._onChange);
    this._control!.addEventListener('blur', this._onBlur);
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width !== undefined && width !== this._lastWidth) { this._lastWidth = width; this._autosize(); }
      });
      this._resizeObserver.observe(this._control!);
    }
  }
  protected _unbind(): void {
    this._control?.removeEventListener('input', this._onInput);
    this._control?.removeEventListener('change', this._onChange);
    this._control?.removeEventListener('blur', this._onBlur);
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;
  }
  protected _formState(): string { return this.value; }
  protected _formValue(): string { return this.value; }
  protected _reset(): void { this._value = this.defaultValue; }
  protected _restore(state: string): void { this._value = state; }
  protected _constraints(): { flags: Partial<FieldValidity>; message: string } {
    if (this.required && !this.value) return { flags: { valueMissing: true }, message: 'Please fill out this field.' };
    if (this.value && this.minlength >= 0 && this.value.length < this.minlength) return { flags: { tooShort: true }, message: `Use at least ${this.minlength} characters.` };
    if (this.maxlength >= 0 && this.value.length > this.maxlength) return { flags: { tooLong: true }, message: `Use no more than ${this.maxlength} characters.` };
    return { flags: {}, message: '' };
  }
}
if (!customElements.get('qds-textarea')) customElements.define('qds-textarea', QdsTextarea);
